import { useStores } from '@src/Common/Hooks/useStores';
import CustomLoading from '@src/Components/Atoms/CustomLoading/CustomLoading';
import SkeletonDetailEventItem from '@src/Components/Atoms/Skeleton/SkeletonDetailEventItem/SkeletonDetailEventItem';
import { TEventType } from '@src/Domain/MySubmission/MySubmission.interface';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef } from 'react';
import DetailEventItem from '../DetailEventItem/DetailEventItem';
import styles from './AllEventList.module.scss';

const AllEventSkeleton: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (isLoading)
    return (
      <div className={styles['LoadingContainer']}>
        {Array.from({ length: 5 }, (_v, k) => (
          <SkeletonDetailEventItem key={k} />
        ))}
      </div>
    );
  return null;
};

const LoadingPrevious: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (isLoading)
    return (
      <div className={styles['LoadingPreviousContainer']}>
        <CustomLoading />
      </div>
    );
  return null;
};

const AllEventList = () => {
  const { AllEventStore } = useStores();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;

    if (container) {
      // Calculate the scroll position
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const spaceToScroll = 20;
      // Check if the user has reached the bottom of the container
      if (
        scrollTop + clientHeight > scrollHeight - spaceToScroll &&
        !AllEventStore.statusPreviousEvent.isLoading
      ) {
        // check if has previousId
        if (AllEventStore.canLoadMoreEvents) {
          AllEventStore.loadPreviousEvent();
        }
      }
    }
  }, [AllEventStore]);

  useEffect(() => {
    const container = containerRef.current;

    // Attach the scroll event listener to the container
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div
      ref={containerRef}
      data-testid="all-event-list"
      className={
        styles['ContainerEventList'] +
        ' ' +
        (AllEventStore.status.isLoading ? styles['isLoading'] : '')
      }
    >
      <AllEventSkeleton isLoading={AllEventStore.status.isLoading} />

      {!AllEventStore.status.isLoading && AllEventStore.data.length > 0 ? (
        <>
          {AllEventStore.data.map((item) => (
            <div className={styles['EventList']} key={item.centuryName}>
              <div className={styles['Title']}>{item.centuryName}</div>
              <div className={styles['Event']}>
                {item.data.map((eventData, idx) => (
                  <DetailEventItem
                    key={eventData.id}
                    id={idx}
                    latlon={[eventData.lan, eventData.lat]}
                    investigation={eventData.eventName}
                    date={eventData.eventDate.toISOString()}
                    locationName={eventData.city}
                    status={undefined}
                    type={eventData.type as TEventType}
                  />
                ))}
              </div>
            </div>
          ))}
          <LoadingPrevious
            isLoading={AllEventStore.statusPreviousEvent.isLoading}
          />
        </>
      ) : (
        <p className={styles['TextEmpty']}>There are no events at this time.</p>
      )}
    </div>
  );
};

export default observer(AllEventList);
